/**
 * Microsoft's sign-in in a popup, for "Confirm with Microsoft"
 * (`plans/outlook-sync.md`, question 3) and for the check before a person
 * links a SharePoint folder (`plans/sharepoint-sync.md`, 2026-10-19). A popup,
 * as Google Drive's, so the screen behind keeps its state.
 */

/** What the callback page posts back to the window that opened it. */
export type MicrosoftCallbackMessage = {
  source: "gb-microsoft";
  code?: string;
  state?: string;
  error?: string;
};

/** How long a closed popup is given to deliver the message it already posted (see Drive's). */
const CLOSED_GRACE_MS = 600;

/**
 * Open Microsoft's sign-in and resolve with the code it sends back. The
 * message is taken from our own origin only: any other page could post a
 * forged code into this window.
 */
export function runMicrosoftPopup(url: string): Promise<{ code: string; state: string }> {
  return new Promise((resolve, reject) => {
    const popup = window.open(url, "gb-microsoft", "width=520,height=680");
    if (!popup) {
      reject(new Error("Your browser blocked the Microsoft sign-in window."));
      return;
    }
    const onMessage = (event: MessageEvent<MicrosoftCallbackMessage>): void => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.source !== "gb-microsoft") return;
      cleanup();
      if (event.data.error || !event.data.code || !event.data.state) {
        reject(new Error("The Microsoft sign-in was cancelled."));
        return;
      }
      resolve({ code: event.data.code, state: event.data.state });
    };
    // A closed popup is given a moment: the callback posts, then closes, and
    // the close can be seen first (`runConsentPopup` in `connectors-api.ts`).
    let giveUp: number | undefined;
    const poll = window.setInterval(() => {
      if (!popup.closed || giveUp !== undefined) return;
      giveUp = window.setTimeout(() => {
        cleanup();
        reject(new Error("The Microsoft sign-in was cancelled."));
      }, CLOSED_GRACE_MS);
    }, 250);
    function cleanup(): void {
      window.clearInterval(poll);
      if (giveUp !== undefined) window.clearTimeout(giveUp);
      window.removeEventListener("message", onMessage);
      if (!popup!.closed) popup!.close();
    }
    window.addEventListener("message", onMessage);
  });
}
